import client from './client';
import { Order, Store } from '../types';

export interface DashboardStats {
    totalSales: number;
    totalOrders: number;
    pendingOrders: number;
    totalItemQuantity: number;
    pendingItemQuantity: number;
    totalProducts: number;
}

export interface OrderSheetSummary {
    id: string;
    label: string;
    deliveryDate: string;
    storeName: string;
    orderCount: number;
    totalItems: number;
    totalAmount: number;
}

export interface ProductSummary {
    id: string;
    name: string;
    totalQuantity: number;
    totalAmount: number;
}

export interface ChartDataPoint {
    date: string;
    completedAmount: number;
    pendingAmount: number;
    cancelledAmount: number;
    completedQuantity: number;
    pendingQuantity: number;
    cancelledQuantity: number;
}

export interface WeeklySalesDataPoint extends Omit<ChartDataPoint, 'date'> {
    weekStart: string;
    weekEnd: string;
    label: string;
}

export interface MonthlySalesDataPoint extends Omit<ChartDataPoint, 'date'> {
    monthStart: string;
    monthEnd: string;
    label: string;
}

export interface CustomerCancellationStat {
    customerName: string;
    customerPhone: string;
    cancelCount: number;
    cancelAmount: number;
}

export interface HourlyStat {
    hour: number;
    count: number;
    quantity: number;
}

export const getDashboardStats = async (storeId?: string): Promise<DashboardStats> => {
    const response = await client.get('dashboard/stats', { params: { storeId } });
    return response.data;
};

export const getSalesChartData = async (days: number = 7, storeId?: string): Promise<ChartDataPoint[]> => {
    const response = await client.get(`dashboard/chart`, { params: { days, storeId } });
    return response.data;
};

export const getRecentOrders = async (storeId?: string): Promise<Order[]> => {
    const response = await client.get('dashboard/recent-orders', { params: { storeId } });
    return response.data;
};

export const getFrequentCancellers = async (limit: number = 5, storeId?: string): Promise<CustomerCancellationStat[]> => {
    const response = await client.get('dashboard/frequent-cancellers', { params: { limit, storeId } });
    return response.data;
};

export const getOrderSheetSummaries = async (storeId?: string): Promise<OrderSheetSummary[]> => {
    const response = await client.get('dashboard/order-sheet-summaries', { params: { storeId } });
    return response.data;
};

export const getProductSummaries = async (storeId?: string): Promise<ProductSummary[]> => {
    const response = await client.get('dashboard/product-summaries', { params: { storeId } });
    return response.data;
};

export const getHourlyOrderStats = async (storeId?: string, days: number = 30): Promise<HourlyStat[]> => {
    const response = await client.get('dashboard/hourly-stats', { params: { storeId, days } });
    return response.data;
};

export interface DashboardSummary {
    stats: DashboardStats;
    recentOrders: any[];
    chartData: ChartDataPoint[];
    weeklySalesData: WeeklySalesDataPoint[];
    monthlySalesData: MonthlySalesDataPoint[];
    frequentCancellers: CustomerCancellationStat[];
    orderSheetSummaries: OrderSheetSummary[];
    productSummaries: ProductSummary[];
    hourlyStats: HourlyStat[];
}

export const getDashboardSummary = async (storeId?: string, weeklyWeeks?: number, monthlyMonths?: number): Promise<DashboardSummary> => {
    const response = await client.get('dashboard/summary', { params: { storeId, weeklyWeeks, monthlyMonths } });
    return response.data;
};

export interface WeeklyFocusReport {
    stores: Store[];
    period: {
        startDate: string;
        endDate: string;
        previousStartDate: string;
        previousEndDate: string;
        label: string;
        isPartial: boolean;
    };
    kpis: {
        activeRevenue: number;
        activeOrders: number;
        averageOrderValue: number;
        medianOrderValue: number;
        cancelledOrders: number;
        cancelledRevenue: number;
    };
    statusBreakdown: Array<{
        status: 'PENDING' | 'READY' | 'COMPLETED';
        orderCount: number;
        revenue: number;
    }>;
    weekOverWeek: Array<{
        label: string;
        previous: number;
        current: number;
        changeRate: number;
    }>;
    dailySales: Array<{
        date: string;
        label: string;
        day: string;
        revenue: number;
        orderCount: number;
        cancelledRevenue: number;
    }>;
    revenueTrend: Array<{
        weekStart: string;
        label: string;
        revenue: number;
        orderCount: number;
    }>;
    storeAnalysis: Array<{
        storeId: string;
        name: string;
        revenue: number;
        orders: number;
        revenueShare: number;
        averageOrderValue: number;
        changeRate: number;
    }>;
    customerPattern: {
        purchasingCustomers: number;
        existingCustomers: number;
        newCustomers: number;
        existingCustomerRevenueShare: number;
        averageBasketSkuCount: number;
        averageBasketQuantity: number;
    };
    topProducts: Array<{
        productId: string | null;
        name: string;
        quantity: number;
        revenue: number;
    }>;
    categorySales: Array<{
        name: string;
        revenue: number;
        quantity: number;
    }>;
    funnel: {
        productViews: number;
        viewedProducts: number;
        sessions: number;
        convertedSessions: number;
        conversionRate: number;
        storeConversion: Array<{
            storeId: string;
            name: string;
            sessions: number;
            convertedSessions: number;
            conversionRate: number;
        }>;
    };
    actionItems: string[];
    conclusion: string;
}

export const getWeeklyFocusReport = async (params?: {
    storeId?: string;
    startDate?: string;
    endDate?: string;
}): Promise<WeeklyFocusReport> => {
    const response = await client.get('dashboard/weekly-focus-report', {
        params,
    });
    return response.data;
};
